// sim/rarityPerks.ts — RARITY AS CHARACTER, not just bigger numbers.
//
// Each gene's rarity tier carries a named SPECIAL EFFECT that escalates from a flat stat
// (Common) into qualitative, build-defining behaviour (Legendary) — so a rare drop reads
// as "this changes how my colony plays", not "this is the same thing with a larger
// number". Authored per BODY PART (GENE_TYPE) so the effect fits what that part does:
//   Wing→flight/scouting, Mandible→war, VenomGland→toxin, Carapace→defense,
//   Antenna→sensing, Limb→gathering (the worker-economy ladder from the brief).
//
// PURE DATA — no logic, no RNG, no sim mutation, never ticked or hashed. Indexed
// positionally by GENE_TYPE × RARITY (../types.ts), the same hard contract geneData.ts
// uses. Consumed by display layers (box reveal, armory, gene inventory) and a stable `id`
// is provided per perk so a future sim layer can gate a REAL effect off it without
// re-authoring the copy. Grounded in docs/LORE.md.

import { GENE_TYPE_NAME, RARITY_NAME } from './geneData.ts';

/** One rarity tier's effect for one gene type. */
export interface RarityPerk {
  /** Stable key for future sim wiring, e.g. "limb.rare". Never shown to the player. */
  id: string;
  /** Short effect name, e.g. "Trail Catalyst". */
  name: string;
  /** Player-facing one-line effect. */
  effect: string;
  /** false ⇒ a flat stat bump (Common); true ⇒ a qualitative special effect. */
  special: boolean;
}

// Readable id stems (slugs) per GENE_TYPE / RARITY index — kept local so the ids stay
// stable even if the display names are reworded.
const TYPE_KEY = ['wing', 'mandible', 'venom', 'carapace', 'antenna', 'limb'] as const;
const RARITY_KEY = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;

/** Build a perk, deriving `id` from the type/rarity slugs and `special` from the tier. */
function perk(type: number, rarity: number, name: string, effect: string): RarityPerk {
  return { id: `${TYPE_KEY[type]}.${RARITY_KEY[rarity]}`, name, effect, special: rarity > 0 };
}

/**
 * RARITY_PERKS[geneType][rarity] — the escalating effect ladder per body part. Common is a
 * flat stat; every tier above it is a qualitative special effect that grows in ambition
 * (a passive twist → a colony-wide system). The Limb ladder is the worker-economy
 * progression from the brief (efficiency → trail → duplication → depot network).
 */
export const RARITY_PERKS: readonly (readonly RarityPerk[])[] = [
  // Wing — flight, speed, scouting reach.
  [
    perk(0, 0, 'Lift', '+Flight speed and travel range.'),
    perk(0, 1, 'Updraft', 'Takeoffs briefly hasten nearby kin.'),
    perk(0, 2, 'Scout Draft', 'Wingbeats scatter scouting scent, widening the colony’s vision.'),
    perk(0, 3, 'Second Wind', 'Sustained flight occasionally grants a free aerial dash.'),
    perk(0, 4, 'Sky Relay', 'Flyers form airborne relays that ferry kin across the map.'),
  ],
  // Mandible — bite, war.
  [
    perk(1, 0, 'Bite', '+Bite force.'),
    perk(1, 1, 'Sunder', 'Strikes briefly sunder a foe’s armor.'),
    perk(1, 2, 'Bloodcall', 'Killing blows rally nearby soldiers into a frenzy.'),
    perk(1, 3, 'Cleave', 'Crushing hits occasionally cleave a second target.'),
    perk(1, 4, 'War Pack', 'Spawns autonomous warfront packs that hunt intruders.'),
  ],
  // Venom Gland — toxin.
  [
    perk(2, 0, 'Potency', '+Toxic potency.'),
    perk(2, 1, 'Lingering Dose', 'Venom lingers, poisoning targets over time.'),
    perk(2, 2, 'Contagion', 'Toxins spread on contact between enemies.'),
    perk(2, 3, 'Rendering', 'Lethal doses occasionally render extra biomass from a kill.'),
    perk(2, 4, 'Toxic Caches', 'Seeds toxic caches that poison whole regions of intruders.'),
  ],
  // Carapace — armor, defense.
  [
    perk(3, 0, 'Plating', '+Armor plating.'),
    perk(3, 1, 'Bulwark', 'Absorbs the first hit of each skirmish.'),
    perk(3, 2, 'Moltbed', 'Shed plates fertilize trails, regenerating passers-by.'),
    perk(3, 3, 'Riposte', 'Heavy blows occasionally reflect damage to the attacker.'),
    perk(3, 4, 'Bastion', 'Anchors fortified bastions that shelter all kin nearby.'),
  ],
  // Antenna — sensing, information.
  [
    perk(4, 0, 'Acuity', '+Sense range.'),
    perk(4, 1, 'Foresense', 'Pierces fog to detect threats early.'),
    perk(4, 2, 'Shared Sight', 'Kin see whatever a single scout sees.'),
    perk(4, 3, 'Bloomsense', 'Occasionally foretells resource blooms before they surface.'),
    perk(4, 4, 'Hive Mind', 'Weaves a colony-wide sensory network.'),
  ],
  // Limb — the worker-economy ladder (matches the brief's examples).
  [
    perk(5, 0, 'Efficiency', '+Worker efficiency — gathers a little faster.'),
    perk(5, 1, 'Sure Grip', 'Carries extra and spills less on every haul.'),
    perk(5, 2, 'Trail Catalyst', 'Food carried also generates trail strength.'),
    perk(5, 3, 'Bounty', 'Workers occasionally duplicate gathered resources.'),
    perk(5, 4, 'Depot Network', 'Workers establish supply depots that autonomously network the colony.'),
  ],
];

/**
 * The perk for a (gene type, rarity) pair, or null when either index is out of range
 * (so callers can guard a malformed gene without throwing). Pure lookup.
 */
export function rarityPerk(geneType: number, rarity: number): RarityPerk | null {
  const row = RARITY_PERKS[geneType];
  if (!row) return null;
  return row[rarity] ?? null;
}

// Import-time shape guard: one perk row per gene type, one perk per rarity, ids unique.
// Pure data comparison (mirrors geneData.ts's length assertions) — catches a mis-edit
// the moment the module loads rather than as a wrong tooltip in play.
const _assert = (() => {
  if (RARITY_PERKS.length !== GENE_TYPE_NAME.length)
    throw new Error(`RARITY_PERKS has ${RARITY_PERKS.length} rows, expected ${GENE_TYPE_NAME.length}`);
  const seen = new Set<string>();
  for (let type = 0; type < RARITY_PERKS.length; type++) {
    const row = RARITY_PERKS[type] as readonly RarityPerk[];
    if (row.length !== RARITY_NAME.length)
      throw new Error(`RARITY_PERKS[${type}] has ${row.length} tiers, expected ${RARITY_NAME.length}`);
    for (let r = 0; r < row.length; r++) {
      const p = row[r] as RarityPerk;
      if (!p.name || !p.effect) throw new Error(`RARITY_PERKS[${type}][${r}] missing name/effect`);
      if (seen.has(p.id)) throw new Error(`RARITY_PERKS duplicate id "${p.id}"`);
      seen.add(p.id);
      if ((r === 0) === p.special) throw new Error(`RARITY_PERKS[${type}][${r}] special flag wrong for tier`);
    }
  }
})();
void _assert;
